import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import { useAppSelector } from '@/hooks';
import { flex } from '@/styles/theme';
import { otherColors } from '@/styles/colors';
import { ChevronRight } from '@/assets/icons';
import { SIDEBAR_DELAY, Z_INDEX_CALENDAR_LEGEND } from '@/config/variables';
import { classList } from '@/helpers';

interface IProps {
  className?: string;
  children: React.ReactNodeArray;
}

const CalendarLegend = ({ className = '', children }: IProps): React.ReactElement => {
  const { t } = useTranslation();
  const { isMobileScreenSize } = useAppSelector(state => state.utils);
  const [isCollapsed, setIsCollapsed] = useState<boolean>(false);

  useEffect(() => {
    if (isMobileScreenSize && !isCollapsed) {
      setIsCollapsed(true);
    }
  }, [isMobileScreenSize]);

  return (
    <Container classes={classList(className, isCollapsed && 'collapsed', isMobileScreenSize && 'is-mobile-screen')}>
      <ToggleButton onClick={() => setIsCollapsed(!isCollapsed)} />
      <LegendLabel>{t`common.legendLabel`}</LegendLabel>
      {React.Children.map(children, child => {
        if (React.isValidElement(child)) {
          return React.cloneElement(child, {});
        }
        return child;
      })}
    </Container>
  );
};

const Container = styled.div.attrs<{ classes?: string }>(({ classes }) => ({
  className: `${flex.rowCenter} justify-content-around ${classes ? classes : ''}`,
}))<{ classes?: string }>`
  align-items: start;
  position: fixed;
  right: 0;
  bottom: 5px;
  min-height: 48px;
  padding-left: 25px;
  padding-right: 15px;
  z-index: ${Z_INDEX_CALENDAR_LEGEND};

  background: #fff;
  border: 1px solid #ecebeb;
  border-radius: 4px;

  font-size: 14px;
  line-height: 17px;
  color: #5a5a5a;

  transition: min-height ${SIDEBAR_DELAY}ms ease-in-out;

  &.is-mobile-screen {
    min-height: 32px;
    flex-direction: column !important;
  }

  &.apply-right-offset {
    right: 300px;
  }
`;

const LegendItem = styled.div.attrs({
  className: `${flex.rowCenter}`,
})`
  padding: 0 10px;
  width: 100%;
  min-height: 48px;
  white-space: nowrap;

  .collapsed & {
    display: none !important;
  }

  .is-mobile-screen & {
    align-items: flex-start !important;
    min-height: 32px;
    padding-top: 10px;

    &:last-child {
      padding-bottom: 10px;
    }
  }
`;

const LegendIcon = styled.div<{ bgColor?: string }>`
  width: 24px;
  height: 24px;
  border-radius: 12px;
  background-color: ${({ bgColor }) => bgColor || '#fff'};

  background-position: 4px;
  margin-right: 14px;
`;

const ToggleButton = styled.button`
  position: absolute;
  left: -18px;
  z-index: 100;

  width: 34px;
  height: 34px;

  background-color: #fff;
  border-radius: 100%;
  border: 1px solid #ecebeb;

  padding: 0;
  display: flex;
  justify-content: center;
  align-items: center;

  transition: background-color ${SIDEBAR_DELAY}ms ease-in-out, transform ${SIDEBAR_DELAY}ms;

  &::before {
    content: '';
    display: block;
    height: 14px;
    width: 14px;
    background-color: ${otherColors.lxc.blue};
    mask-image: url(${ChevronRight});
    mask-repeat: no-repeat;
    mask-position: center center;
    mask-size: contain;
  }

  .collapsed & {
    transform: rotate3d(0, 0, 1, 180deg);
    mask-position: center center;
  }
`;

const LegendLabel = styled.div`
  display: none;
  width: 80px;
  text-align: center;

  .collapsed & {
    display: block;
  }
`;

CalendarLegend.Item = LegendItem;
CalendarLegend.Icon = LegendIcon;

export { CalendarLegend };
